import type { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, inject, signal, viewChild, type WritableSignal } from '@angular/core';
import { FormControl, type FormControlStatus, FormGroup } from '@angular/forms';

import { firstValueFrom } from 'rxjs';

import {
  type DefineFormState,
  DynamicFormDefineComponent,
  DynamicFormRespondComponent,
  RespondSelectSingleComponent,
} from '@core/ask-hr-dynamic-forms-smart-ui';
import type { DynamicFormAnswerConfig, DynamicFormConfig } from '@core/ask-hr-dynamic-forms-types';
import { AskTemplatesDataService, PriorityService } from '@core/ask-hr-templates-logic';
import type {
  AskPredefinedTemplate,
  AskTemplate,
  CreateAskTemplate,
} from '@core/ask-hr-templates-types';
import type { AskTicketPriority } from '@core/ask-hr-types';
import { formSignalStatus, formSignalValue } from '@core/common-legacy';
import { InfoboxComponent } from '@core/components-infobox-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { ProsemirrorBasicConfig } from '@core/prosemirror-legacy/webapp';
import type { EditorJSONState } from '@core/prosemirror-types';
import {
  ButtonComponent,
  DialogBodyComponent,
  DialogComponent,
  DialogHeaderComponent,
  RtTooltip,
  type SegmentedButtonType,
  SegmentedControlComponent,
} from '@core/user-interface-legacy';
import { deepEqual, generateUuid } from '@core/utils-legacy';

import { AskTemplateCardComponent } from './ask-template-card/ask-template-card.component';
import { AskTemplateFormComponent } from '../ask-template-form/ask-template-form.component';
import {
  type AskTemplateFormState,
  AskTemplatesFormService,
  FormKindProviders,
} from '../ask-templates-form.service';

export type AskTemplatesDialogViews = 'form' | 'preview';

@Component({
  selector: 'ask-templates-dialog',
  templateUrl: './ask-templates-dialog.component.html',
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    DynamicFormDefineComponent,
    TranslatePipe,
    AskTemplateFormComponent,
    SegmentedControlComponent,
    ButtonComponent,
    InfoboxComponent,
    DynamicFormRespondComponent,
    AskTemplateCardComponent,
    RespondSelectSingleComponent,
    RtTooltip,
  ],
  styleUrls: ['./ask-templates-dialog.component.less'],
  providers: [...FormKindProviders, [ProsemirrorBasicConfig, AskTemplatesFormService]],
})
export class AskTemplatesDialogComponent extends DialogComponent<
  {
    template?: AskTemplate | AskPredefinedTemplate | null;
    templateCreated?: WritableSignal<boolean>;
    firstView: AskTemplatesDialogViews;
  },
  void
> {
  private readonly askTemplatesFormService = inject(AskTemplatesFormService);
  private readonly templatesDataService = inject(AskTemplatesDataService);
  private readonly i18n = inject(I18nService);
  private readonly dynamicFormDefine = viewChild(DynamicFormDefineComponent);

  protected serverErrors = signal<Record<string, Record<string, string>>>({});
  protected errors = computed(() => ({
    name: {
      maxlength: this.i18n.translate('tlc.ask_hr.form_template_create.errors.max_length', {
        limit: 255,
      }),
      ...(this.serverErrors().name ?? {}),
    },
  }));
  protected priorityControl = new FormGroup({
    value: new FormControl<AskTicketPriority | null>(null, { nonNullable: true }),
  });
  protected priorityConfig = inject(PriorityService).getPriorityConfig({ isDisabled: true });
  protected dynamicFormStatus = signal<FormControlStatus[]>([]);
  protected activeView = signal<AskTemplatesDialogViews>(this.dialogData.firstView);
  protected switcherButtons = computed((): Array<SegmentedButtonType<'form' | 'preview'>> => {
    const activeView = this.activeView();
    return [
      {
        value: 'form',
        label: this.i18n.translate('tlc.ask_hr.form_template_create.form'),
        iconName: 'edit',
        isActive: activeView === 'form',
        isAvailable: true,
      },
      {
        value: 'preview',
        label: this.i18n.translate('tlc.ask_hr.form_template_create.preview'),
        iconName: 'remove-red-eye',
        isActive: activeView === 'preview',
        isAvailable: true,
      },
    ];
  });
  protected askTemplateForm = this.askTemplatesFormService.createAskTemplateForm(
    this.dialogData?.template,
  );
  protected defineFormState = signal<AskTemplateFormState[]>([], { equal: deepEqual });
  protected initialDefineFormState = signal<AskTemplateFormState[]>([], { equal: deepEqual });
  protected respondFormConfig = computed((): DynamicFormAnswerConfig[] =>
    this.defineFormState().map(item => ({
      kind: item.kind,
      hash: item.hash,
      config: { ...item.value, disabled: true },
    })),
  );
  protected askTemplateValue = formSignalValue(this.askTemplateForm);
  protected askTemplateStatus = formSignalStatus(this.askTemplateForm);
  protected submitButtonDisabled = computed(
    (): boolean =>
      this.askTemplateStatus() !== 'VALID' ||
      this.dynamicFormStatus().some(status => status !== 'VALID'),
  );

  public ngOnInit(): void {
    this.initialDefineFormState.set(
      (this.dialogData.template?.form.items ?? []).map(item =>
        this.askTemplatesFormService.askTemplateFromDto(item),
      ),
    );
    this.defineFormState.set(this.initialDefineFormState());
  }

  public onDynamicFormChanged(state: Array<DefineFormState<DynamicFormConfig>>) {
    this.defineFormState.set(<AskTemplateFormState[]>state);
  }

  public async save() {
    const value = this.askTemplateForm.getRawValue();
    const defineFormState = this.defineFormState();
    const data: CreateAskTemplate = {
      name: value.name,
      symbol: value.symbol,
      active: value.active,
      autoAssignments: value.autoAssignments.map(memberGuid => ({ memberGuid })),
      descriptionJson: <EditorJSONState>value.descriptionJson!,
      prioritySelectionEnabled: value.prioritySelectionEnabled,
      form: {
        hash: generateUuid(),
        items: defineFormState.map(item => this.askTemplatesFormService.askTemplateToDto(item)),
      },
    };
    const template = this.dialogData.template;
    const id = template ? 'id' in template && template.id : false;
    this.clearServerErrors();
    if (id) {
      const result = await firstValueFrom(
        this.templatesDataService.updateTemplate({ ...data, id }),
      );
      if (result.isSuccess) {
        this.askTemplateForm.markAsPristine();
        this.askTemplateForm.markAsUntouched();
        this.dynamicFormDefine()
          ?.getFromComponents()
          .forEach(component => {
            component.inputs.defineForm.markAsPristine();
            component.inputs.defineForm.markAsUntouched();
          });
        this.dialogRef.close();
      } else {
        this.handleError(result.failed);
      }
    } else {
      const result = await firstValueFrom(this.templatesDataService.createTemplate(data));
      if (result.isSuccess) {
        this.dialogData.templateCreated?.set(true);
        this.dialogRef.close();
      } else {
        this.handleError(result.failed);
      }
    }
  }

  protected onViewSwitch(value: AskTemplatesDialogViews) {
    if ('preview' === value) {
      this.initialDefineFormState.set(this.defineFormState());
    }
    this.activeView.set(value);
  }

  private handleError(errResponse: HttpErrorResponse) {
    const nameFieldError = errResponse.error.errors?.name?.[0];
    if (nameFieldError) {
      this.serverErrors.set({
        name: { server: nameFieldError },
      });
      this.askTemplateForm.controls.name.setErrors({ server: true });
    }
  }

  private clearServerErrors() {
    this.serverErrors.set({});
    this.askTemplateForm.controls.name.setErrors(null);
    this.askTemplateForm.controls.name.updateValueAndValidity();
  }
}
